import type { CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import { quotePath, quoteSearch, type ChallengeId, type ScopeIntentId } from "@/lib/quote-links";
import { trackMarketingEvent } from "@/lib/analytics";

export type EntryOfferId = "performance" | "cro-seo" | "custom";

type EntryAccent = {
  accent: string;
  line: string;
  soft: string;
};

interface EntryOfferBridgeProps {
  locale: "en" | "es";
  path: string;
  intent?: ScopeIntentId;
  challenge?: ChallengeId;
}

export function resolveEntryOffer(path: string): EntryOfferId {
  const normalized = path.toLowerCase();
  if (normalized.includes("performance") || normalized.includes("rendimiento")) return "performance";
  if (
    normalized.includes("cro") ||
    normalized.includes("technical-seo") ||
    normalized.includes("seo-tecnico") ||
    normalized.includes("analytics-ai") ||
    normalized.includes("analitica-ai")
  ) return "cro-seo";
  return "custom";
}

// Structural surfaces (background tint and hairlines) stay on the neutral
// platform tokens. Only the capability accent changes per page, and it is used
// on small markers: the feature checks and the section eyebrow.
const NEUTRAL_SURFACE = {
  line: "var(--theme-platform-line)",
  soft: "var(--theme-platform-soft)",
} as const;

function resolveCapabilityAccent(path: string): string {
  const normalized = path.toLowerCase();

  if (normalized.includes("performance") || normalized.includes("rendimiento") || normalized.includes("cro")) {
    return "var(--theme-growth-accent)";
  }
  if (normalized.includes("technical-seo") || normalized.includes("seo-tecnico")) {
    return "var(--theme-seo-accent)";
  }
  if (normalized.includes("integration") || normalized.includes("integraciones")) {
    return "var(--theme-systems-accent)";
  }
  if (normalized.includes("analytics-ai") || normalized.includes("analitica-ai")) {
    return "var(--theme-data-accent)";
  }
  if (normalized.includes("shopify-plus") || normalized.includes("agencia-shopify-plus")) {
    return "var(--theme-editorial-accent)";
  }
  return "var(--theme-platform-accent)";
}

export function resolveEntryAccent(path: string): EntryAccent {
  return { accent: resolveCapabilityAccent(path), ...NEUTRAL_SURFACE };
}

const COPY = {
  en: {
    eyebrow: "Commercial starting point",
    title: "This capability starts through one of three entry projects.",
    body: "The page explains a specialist capability. It does not create another package. The written scope selects only the work needed to resolve and validate the current constraint.",
    label: "Recommended entry",
    cta: "Request this scope",
    pricing: "Review all pricing",
    pricingPath: "/pricing",
    offers: {
      performance: {
        name: "Performance Fix",
        price: "$2,500–$4,000",
        description: "For storefront speed, field performance, app contention and unreliable mobile interaction.",
        includes: ["Comparable baseline", "Root-cause map", "Staged implementation", "Before/after validation"],
      },
      "cro-seo": {
        name: "CRO & Technical SEO Fix",
        price: "$3,000–$5,000",
        description: "For measurable conversion, crawling, indexation, content ownership or commercial measurement constraints.",
        includes: ["Funnel or search diagnosis", "Prioritized implementation", "Theme-level changes", "Measurement and validation plan"],
      },
      custom: {
        name: "Custom Build or Replatform",
        price: "From $5,000",
        description: "For themes, migrations, Plus architecture, apps and integrations requiring controlled implementation and release.",
        includes: ["Architecture and ownership", "Written implementation scope", "Staging and QA", "Launch, rollback and handoff"],
      },
    },
  },
  es: {
    eyebrow: "Punto de entrada comercial",
    title: "Esta capacidad empieza mediante uno de tres proyectos de entrada.",
    body: "La página explica una capacidad especializada; no crea otro paquete. El alcance escrito selecciona únicamente el trabajo necesario para resolver y validar la restricción actual.",
    label: "Entrada recomendada",
    cta: "Solicitar este alcance",
    pricing: "Revisar todos los precios",
    pricingPath: "/es/precios",
    offers: {
      performance: {
        name: "Fix de Performance",
        price: "$2,500–$4,000 USD",
        description: "Para velocidad del storefront, datos de campo, contención de apps e interacción móvil poco fiable.",
        includes: ["Línea base comparable", "Mapa de causa raíz", "Implementación en staging", "Validación antes/después"],
      },
      "cro-seo": {
        name: "Fix de CRO y SEO Técnico",
        price: "$3,000–$5,000 USD",
        description: "Para restricciones medibles de conversión, crawling, indexación, ownership de contenido o medición comercial.",
        includes: ["Diagnóstico de funnel o búsqueda", "Implementación priorizada", "Cambios en theme", "Plan de medición y validación"],
      },
      custom: {
        name: "Build Custom o Replatform",
        price: "Desde $5,000 USD",
        description: "Para themes, migraciones, arquitectura Plus, apps e integraciones que requieren implementación y release controlados.",
        includes: ["Arquitectura y ownership", "Alcance escrito", "Staging y QA", "Launch, rollback y handoff"],
      },
    },
  },
} as const;

export function EntryOfferBridge({ locale, path, intent, challenge }: EntryOfferBridgeProps) {
  const copy = COPY[locale];
  const offerId = resolveEntryOffer(path);
  const offer = copy.offers[offerId];
  const accent = resolveEntryAccent(path);
  const search = quoteSearch({ intent, challenge, source: path });
  const style = {
    "--entry-accent": accent.accent,
    "--entry-line": accent.line,
    "--entry-soft": accent.soft,
  } as CSSProperties;

  return (
    <section style={style} className="border-y border-[var(--entry-line)] bg-[var(--entry-soft)]">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[0.78fr_1.22fr] lg:items-start lg:py-20">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--entry-accent)]">{copy.eyebrow}</p>
          <h2 className="mt-4 max-w-xl text-balance font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">{copy.title}</h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{copy.body}</p>
        </div>
        <article className="border-y border-[var(--entry-line)] py-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--entry-accent)]">{copy.label}</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
            <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground">{offer.name}</h3>
            <p className="shrink-0 text-lg font-semibold text-foreground">{offer.price}</p>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{offer.description}</p>
          {path === "/shopify-migration" ? (
            <div className="mt-5 space-y-2 border-l-2 border-[var(--entry-line)] pl-4 text-sm leading-relaxed text-muted-foreground">
              <p>Entry / bounded replatform starts at $5,000.</p>
              <p>Typical mid-catalog migrations are scoped after audit based on data quality, URL count, and integrations.</p>
              <p>Shopify Plus + ERP/B2B/multi-store is a separate architecture path: <Link to="/shopify-plus-agency" className="font-semibold text-foreground underline underline-offset-4">Shopify Plus Agency</Link>.</p>
              <p>The $5,000 entry point is not the average price of an enterprise Magento migration.</p>
            </div>
          ) : null}
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {offer.includes.map((item) => <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-foreground/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--entry-accent)]" />{item}</li>)}
          </ul>
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link to={quotePath(locale)} search={search} onClick={() => trackMarketingEvent("marketing_cta_click", { cta_type: "entry_scope", cta_location: "entry_offer_bridge", source_page: path, entry_project: offerId, challenge: challenge ?? null, locale })} className="inline-flex items-center gap-2 text-sm font-semibold text-foreground transition-[gap] hover:gap-3">{copy.cta}<ArrowRight className="h-4 w-4" /></Link>
            <Link to={copy.pricingPath} className="text-sm font-medium text-muted-foreground hover:text-[var(--entry-accent)]">{copy.pricing}</Link>
          </div>
        </article>
      </div>
    </section>
  );
}
